import { useSubjects } from '@/features/subject/subject.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { format, parseISO } from 'date-fns';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { DataTableRowActions } from '@/components/datatable/data-table-row-action';
import { DataTableSimple } from '@/components/datatable/data-table-simple/data-table-simple';
import CreateUpdateSubjectModal from '@/components/modals/create-update-subject-modal';

const subjectRouteSchema = z.object({
  mode: z.enum(['create', 'update']).optional(),
  subject: z.number().optional(),
});

export const Route = createFileRoute('/_app/(subjects)/subjects/')({
  validateSearch: zodSearchValidator(subjectRouteSchema),
  component: Subjects,
});

function Subjects() {
  return (
    <div className='flex h-full flex-col gap-y-2 px-4 py-2'>
      <SubjectsTabHeader />
      <SubjectsTable />
    </div>
  );
}

function SubjectsTabHeader() {
  const navigate = useNavigate();
  const { t } = useTranslation(['subject']);

  const createSubject = () => {
    navigate({ to: '.', search: { mode: 'create' } });
  };

  return (
    <div className='flex flex-row justify-between py-4'>
      <div className='flex flex-col'>
        <span className='text-xl font-bold'>{t('page_title', { format: 'uppercase' })}</span>
        <span className='text-base font-light text-neutral-500'>{t('page_sub_title')}</span>
      </div>
      <div className='flex flex-row space-x-4'>
        <Button className='flex items-center gap-3' onClick={createSubject} size='default'>
          <Plus strokeWidth={4} className='h-6 w-6' />
          {toCapitalize(t('subject', { ns: 'common' }))}
        </Button>
      </div>
      <CreateUpdateSubjectModal />
    </div>
  );
}

const DateColumn = ({ date }: { date: string }) => {
  return <div className='min-w-[50px]'>{format(parseISO(date), 'dd/MM/yyyy HH:mm')}</div>;
};

function SubjectsTable() {
  const { t } = useTranslation(['table', 'common']);
  const navigate = useNavigate();
  const { data = [], isLoading } = useSubjects();

  const updateSubject = (id: number) => {
    navigate({ to: '.', search: { mode: 'update', subject: id } });
  };

  return (
    <DataTableSimple
      data={data}
      columns={['id', 'name', 'createdAt', 'updatedAt']}
      isLoading={isLoading}
      columnsLabels={{
        id: t('serial_id'),
        name: t('name'),
        createdAt: t('created_at'),
        updatedAt: t('updated_at'),
      }}
      customRender={{
        createdAt: (row) => <DateColumn date={row.getValue('createdAt')} />,
        updatedAt: (row) => <DateColumn date={row.getValue('updatedAt')} />,
      }}
      extraColumns={[
        {
          id: 'actions',
          size: 50,
          cell: ({ row }) => (
            <DataTableRowActions
              row={row}
              menuItems={[
                {
                  label: t('edit', { ns: 'common' }),
                  onClick: () => updateSubject(row.getValue('id')),
                  isLoading: false,
                },
              ]}
            />
          ),
        },
      ]}
      className='flex h-full flex-col overflow-y-auto'
    />
  );
}
